import * as React from 'react';
import type { EntityDecision } from 'shared/src/api/ai-media-plan-chat-types';
import type {
  CreateCampaignPayload,
  CreateLineItemPayload
} from 'shared/src/beeswax/beeswax-types';
import type { TargetingPreview } from 'shared/src/api/targeting-types';
import {
  extractPartialFailureDetail,
  formatPartialFailureMessage
} from 'shared/src/api/partial-failure';
import { Button } from '@/components/forms/ui/button';
import { Input } from '@/components/forms/ui/input';
import { trpc } from '@/utils/trpc-client';
import { showErrorToast } from '@/components/feedback/error-toast';
import type { MediaPlanPreviewCardProps } from '../preview-card-registry';
import { ProposalSummarySection } from '../proposal-summary-section';
import { formatLifetimeBudget } from '../workspace-card-registry';
import { CommitCardFrame, type CommitOutcome } from '@/features/ai/shared/commit-card-frame';
import { formatMoney } from 'shared/src/utils/money/money-utils';

export function BeeswaxMediaPlanPreviewCard({
  data,
  strategyId,
  lineItemName,
  onFieldsEdited,
  onCreated,
  nextPlatform,
  onRequestNewPlatform,
  toolCallId,
  externalOutcome,
  onResolve
}: MediaPlanPreviewCardProps): JSX.Element {
  // Confirmed-write transport: when a toolCallId is present the card resolves the createMediaPlan
  // tool via the beeswaxConfirmMediaPlan endpoint; otherwise it uses the legacy direct mutation.
  const isConfirmedWrite = Boolean(toolCallId);
  const campaignDecision = findEntity(data.entities, 'campaign');
  const lineItemDecision = findEntity(data.entities, 'line_item');
  const initialCampaignPayload =
    campaignDecision?.mode === 'create'
      ? (campaignDecision.payload as CreateCampaignPayload)
      : null;
  const existingCampaignId =
    campaignDecision?.mode === 'existing' ? campaignDecision.entity_id : null;
  const initialLineItemPayload =
    lineItemDecision?.mode === 'create'
      ? (lineItemDecision.payload as CreateLineItemPayload)
      : null;

  const [outcome, setOutcome] = React.useState<CommitOutcome | null>(null);
  const [campaignSuccessUrl, setCampaignSuccessUrl] = React.useState<string | null>(null);
  // Legacy-transport failures are non-terminal (the card stays actionable for a retry).
  const [errorMsg, setErrorMsg] = React.useState<string | null>(null);
  const [notCreated, setNotCreated] = React.useState<string[]>([]);
  const [editing, setEditing] = React.useState(false);

  const [campaignPayload, setCampaignPayload] = React.useState<CreateCampaignPayload | null>(
    initialCampaignPayload ? { ...initialCampaignPayload } : null
  );
  const [lineItemPayload, setLineItemPayload] = React.useState<CreateLineItemPayload>(
    initialLineItemPayload ? { ...initialLineItemPayload } : ({} as CreateLineItemPayload)
  );
  const [targetingState, setTargetingState] = React.useState<TargetingPreview | undefined>(
    data.targeting ? structuredClone(data.targeting) : undefined
  );

  const initialCampaignRef = React.useRef(
    initialCampaignPayload ? { ...initialCampaignPayload } : null
  );
  const initialLineItemRef = React.useRef(
    initialLineItemPayload ? { ...initialLineItemPayload } : null
  );

  const legacyMutation = trpc.confirmMediaPlanCreate.useMutation({
    onSuccess: result => {
      setOutcome({ status: 'applied', summary: 'Media plan created in Beeswax' });
      setEditing(false);
      setNotCreated(result.not_created ?? []);
      // The backend returns the env-correct Beeswax deep link on the resolved campaign entity.
      const campUrl = result.resolved_entities.find(r => r.entityType === 'campaign')?.url;
      if (campUrl) setCampaignSuccessUrl(campUrl);
      onCreated?.();
    },
    onError: err => {
      const partial = extractPartialFailureDetail(err);
      const msg = partial ? formatPartialFailureMessage(partial) : err.message;
      setErrorMsg(msg);
      showErrorToast(msg);
    }
  });

  const confirmedWriteMutation = trpc.beeswaxConfirmMediaPlan.useMutation();

  const isPending = legacyMutation.isPending || confirmedWriteMutation.isPending;

  function currentProposal() {
    return {
      account_id: data.account_id ?? '',
      campaign_mode:
        campaignDecision?.mode === 'existing' ? ('existing' as const) : ('create' as const),
      campaign_id:
        campaignDecision?.mode === 'existing' ? Number(campaignDecision.entity_id) : undefined,
      campaign_payload: campaignPayload ?? undefined,
      line_item: {
        ...lineItemPayload,
        bidding: { ...lineItemPayload.bidding, cpm_bid: lineItemPayload.bidding?.cpm_bid ?? 0 }
      },
      targeting: targetingState
        ? {
            modules: targetingState.expression.modules,
            name: targetingState.expression.name
          }
        : undefined
    };
  }

  async function handleCreate() {
    setErrorMsg(null);

    if (isConfirmedWrite && toolCallId && onResolve) {
      try {
        const res = await confirmedWriteMutation.mutateAsync({
          execution_line_item_id: data.execution_line_item_id,
          strategy_id: strategyId,
          platform_id: data.platform_id,
          tool_call_id: toolCallId,
          proposal: currentProposal()
        });
        setOutcome({ status: 'applied', summary: 'Media plan created in Beeswax' });
        setEditing(false);
        setNotCreated(res.result?.not_created ?? []);
        const campUrl = res.result?.resolved_entities.find(r => r.entityType === 'campaign')?.url;
        if (campUrl) setCampaignSuccessUrl(campUrl);
        onCreated?.();
        // Always resolve the tool so the agent resumes.
        await onResolve({
          status: res.status,
          media_plans: res.result?.media_plans,
          resolved_entities: res.result?.resolved_entities,
          warnings: res.result?.warnings,
          not_created: res.result?.not_created
        });
      } catch (err) {
        const partial = extractPartialFailureDetail(err);
        const msg = partial
          ? formatPartialFailureMessage(partial)
          : err instanceof Error
            ? err.message
            : 'Failed to create media plan';
        setOutcome({ status: 'failed', summary: msg });
        showErrorToast(msg);
        // Feed the error back to the agent so it can suggest a remediation and re-propose a corrected
        // card (the user isn't stranded — a fresh card replaces this one). The agent is prompted to fix
        // fixable inputs and re-propose once, or relay guidance for partial/system failures.
        await onResolve({ status: 'failed', error: msg });
      }
      return;
    }

    const campaignEntity: EntityDecision =
      campaignDecision?.mode === 'create' && campaignPayload
        ? { mode: 'create', entityType: 'campaign', payload: campaignPayload }
        : {
            mode: 'existing',
            entityType: 'campaign',
            entity_id: campaignDecision?.mode === 'existing' ? campaignDecision.entity_id : ''
          };
    const lineItemEntity: EntityDecision = {
      mode: 'create',
      entityType: 'line_item',
      payload: lineItemPayload
    };
    await legacyMutation.mutateAsync({
      execution_line_item_id: data.execution_line_item_id,
      strategy_id: strategyId,
      platform_id: data.platform_id,
      account_id: data.account_id,
      entities: [campaignEntity, lineItemEntity],
      targeting: targetingState
    });
  }

  async function handleCancel() {
    setOutcome({ status: 'cancelled' });
    if (onResolve) await onResolve({ status: 'cancelled' });
  }

  const applied = outcome?.status === 'applied';
  const locked = isPending || outcome !== null || Boolean(externalOutcome);

  function handleToggleEdit() {
    if (editing && onFieldsEdited) {
      const diff = computeEditDiff(
        initialCampaignRef.current,
        campaignPayload,
        initialLineItemRef.current,
        lineItemPayload
      );
      if (diff) onFieldsEdited(diff);
    }
    setEditing(!editing);
  }

  const scope =
    campaignDecision?.mode === 'create'
      ? 'campaign + line item + media buy + media plan'
      : 'line item + media buy + media plan';

  return (
    <>
      <CommitCardFrame
        tier="live"
        badgeLabel="Writes to Beeswax"
        title="Create media plan in Beeswax"
        consequence={`Creates ${scope} in Beeswax — created inactive; nothing spends until activated.`}
        confirmLabel="Create in Beeswax"
        confirmDisabled={editing}
        isPending={isPending}
        outcome={outcome}
        externalOutcome={externalOutcome ?? null}
        toolCallId={toolCallId}
        errorMsg={errorMsg}
        onConfirm={handleCreate}
        onCancel={handleCancel}>
        {editing ? (
          <div className="mb-3 rounded-lg bg-amber-400 px-4 py-2">
            <div className="flex items-center gap-2 text-sm font-semibold text-amber-900">
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Z"
                />
              </svg>
              Edit Mode — modify fields below, then click Done Editing
            </div>
          </div>
        ) : null}

        <div className="mb-3 flex items-center justify-between">
          <div className="text-xs text-gray-500">
            Linking to: <span className="font-medium text-gray-700">{lineItemName}</span>
          </div>
          {!locked ? (
            <Button onClick={handleToggleEdit} variant="outline" size="sm">
              {editing ? 'Done Editing' : 'Edit'}
            </Button>
          ) : null}
        </div>

        {data.summary?.length ? <ProposalSummarySection summary={data.summary} /> : null}

        {campaignDecision?.mode === 'create' && campaignPayload ? (
          <CampaignSection
            payload={campaignPayload}
            onChange={setCampaignPayload}
            editing={editing}
            locked={locked}
          />
        ) : (
          <div className="mb-4 rounded-lg bg-gray-50 p-3">
            <h4 className="mb-1 text-xs font-semibold text-gray-500">EXISTING CAMPAIGN</h4>
            <div className="text-sm">Campaign #{existingCampaignId ?? ''}</div>
          </div>
        )}

        <LineItemSection
          payload={lineItemPayload}
          onChange={setLineItemPayload}
          editing={editing}
          locked={locked}
        />

        {targetingState ? (
          <TargetingSection
            targeting={targetingState}
            onChange={setTargetingState}
            editing={editing}
            locked={locked}
          />
        ) : null}
      </CommitCardFrame>

      {applied ? (
        <div className="mt-2 space-y-2">
          {campaignSuccessUrl || (nextPlatform && onRequestNewPlatform) ? (
            <div className="flex items-center gap-3 px-1">
              {campaignSuccessUrl ? (
                <a
                  href={campaignSuccessUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm font-medium text-primary hover:underline">
                  View in Beeswax &#x2197;
                </a>
              ) : null}
              {nextPlatform && onRequestNewPlatform ? (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => onRequestNewPlatform(nextPlatform.id)}>
                  Create another for {nextPlatform.name}
                </Button>
              ) : null}
            </div>
          ) : null}
          {notCreated.length > 0 ? (
            <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 text-xs text-blue-900">
              <p className="font-semibold">Still to set up in Beeswax</p>
              <ul className="mt-1 list-disc pl-4">
                {notCreated.map((item, i) => (
                  <li key={`not-created-${i}`} className="mt-0.5">
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </div>
      ) : null}
    </>
  );
}

function findEntity(entities: EntityDecision[], entityType: string): EntityDecision | undefined {
  return entities.find(e => e.entityType === entityType);
}

function CampaignSection({
  payload,
  onChange,
  editing,
  locked
}: {
  payload: CreateCampaignPayload;
  onChange: (p: CreateCampaignPayload) => void;
  editing: boolean;
  locked: boolean;
}) {
  function update(field: keyof CreateCampaignPayload, value: string) {
    onChange({ ...payload, [field]: value });
  }

  if (!editing) {
    return (
      <div className="mb-4 rounded-lg bg-gray-50 p-3">
        <h4 className="mb-2 text-xs font-semibold text-gray-500">NEW CAMPAIGN</h4>
        <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
          <ViewField label="Name" value={payload.name} />
          <ViewField label="Budget Type" value={payload.budget_type} />
          <ViewField
            label="Lifetime Budget"
            value={formatLifetimeBudget(payload.spend_budget?.lifetime, payload.budget_type)}
          />
          <ViewField label="Start Date" value={payload.start_date} />
          <ViewField label="End Date" value={payload.end_date} />
        </dl>
      </div>
    );
  }

  return (
    <div className="mb-4 rounded-lg border p-3">
      <h4 className="mb-2 text-xs font-semibold text-gray-500">NEW CAMPAIGN</h4>
      <div className="grid grid-cols-2 gap-2">
        <EditField label="Name">
          <Input
            value={payload.name}
            onChange={e => update('name', e.target.value)}
            disabled={locked}
            className="h-9 text-sm"
          />
        </EditField>
        <EditField label="Budget Type">
          <select
            value={payload.budget_type}
            onChange={e => update('budget_type', e.target.value)}
            disabled={locked}
            className="h-9 w-full rounded-md border px-2 text-sm leading-normal">
            <option value="spend">spend</option>
            <option value="impressions">impressions</option>
            <option value="spend including vendor fees">spend including vendor fees</option>
          </select>
        </EditField>
        <EditField label="Lifetime Budget">
          <Input
            value={payload.spend_budget?.lifetime ?? ''}
            onChange={e =>
              onChange({
                ...payload,
                spend_budget: { ...payload.spend_budget, lifetime: e.target.value }
              })
            }
            disabled={locked}
            className="h-9 text-sm"
          />
        </EditField>
        <EditField label="Start Date">
          <Input
            value={payload.start_date}
            onChange={e => update('start_date', e.target.value)}
            disabled={locked}
            className="h-9 text-sm"
          />
        </EditField>
        <EditField label="End Date">
          <Input
            value={payload.end_date}
            onChange={e => update('end_date', e.target.value)}
            disabled={locked}
            className="h-9 text-sm"
          />
        </EditField>
      </div>
    </div>
  );
}

function LineItemSection({
  payload,
  onChange,
  editing,
  locked
}: {
  payload: CreateLineItemPayload;
  onChange: (p: CreateLineItemPayload) => void;
  editing: boolean;
  locked: boolean;
}) {
  function update(field: keyof CreateLineItemPayload, value: string | number) {
    onChange({ ...payload, [field]: value });
  }

  if (!editing) {
    return (
      <div className="rounded-lg bg-gray-50 p-3">
        <h4 className="mb-2 text-xs font-semibold text-gray-500">BEESWAX LINE ITEM</h4>
        <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
          <ViewField label="Name" value={payload.name} />
          <ViewField label="Type" value={payload.type} />
          <ViewField label="Budget Type" value={payload.budget_type} />
          <ViewField
            label="Lifetime Budget"
            value={formatLifetimeBudget(payload.spend_budget?.lifetime, payload.budget_type)}
          />
          <ViewField label="Bidding Strategy" value={payload.bidding.strategy} />
          <ViewField
            label="CPM Bid"
            value={payload.bidding.cpm_bid != null ? formatMoney(payload.bidding.cpm_bid) : '—'}
          />
          <ViewField label="Start Date" value={payload.start_date} />
          <ViewField label="End Date" value={payload.end_date} />
        </dl>
      </div>
    );
  }

  return (
    <div className="rounded-lg border p-3">
      <h4 className="mb-2 text-xs font-semibold text-gray-500">BEESWAX LINE ITEM</h4>
      <div className="grid grid-cols-2 gap-2">
        <EditField label="Name">
          <Input
            value={payload.name}
            onChange={e => update('name', e.target.value)}
            disabled={locked}
            className="h-9 text-sm"
          />
        </EditField>
        <EditField label="Type">
          <select
            value={payload.type}
            onChange={e => update('type', e.target.value)}
            disabled={locked}
            className="h-9 w-full rounded-md border px-2 text-sm leading-normal">
            <option value="banner">banner</option>
            <option value="video">video</option>
            <option value="native">native</option>
          </select>
        </EditField>
        <EditField label="Budget Type">
          <select
            value={payload.budget_type}
            onChange={e => update('budget_type', e.target.value)}
            disabled={locked}
            className="h-9 w-full rounded-md border px-2 text-sm leading-normal">
            <option value="spend">spend</option>
            <option value="impressions">impressions</option>
            <option value="spend including vendor fees">spend including vendor fees</option>
          </select>
        </EditField>
        <EditField label="Lifetime Budget">
          <Input
            value={payload.spend_budget?.lifetime ?? ''}
            onChange={e =>
              onChange({
                ...payload,
                spend_budget: { ...payload.spend_budget, lifetime: e.target.value }
              })
            }
            disabled={locked}
            className="h-9 text-sm"
          />
        </EditField>
        <EditField label="Bidding Strategy">
          <Input value={payload.bidding.strategy} disabled className="h-9 text-sm" />
        </EditField>
        <EditField label="CPM Bid ($)">
          <Input
            type="number"
            step="0.01"
            value={payload.bidding.cpm_bid ?? ''}
            onChange={e =>
              onChange({
                ...payload,
                bidding: { ...payload.bidding, cpm_bid: parseFloat(e.target.value) || 0 }
              })
            }
            disabled={locked}
            className="h-9 text-sm"
          />
        </EditField>
        <EditField label="Start Date">
          <Input
            value={payload.start_date}
            onChange={e => update('start_date', e.target.value)}
            disabled={locked}
            className="h-9 text-sm"
          />
        </EditField>
        <EditField label="End Date">
          <Input
            value={payload.end_date}
            onChange={e => update('end_date', e.target.value)}
            disabled={locked}
            className="h-9 text-sm"
          />
        </EditField>
      </div>
    </div>
  );
}

function ViewField({ label, value }: { label: string; value: string }) {
  return (
    <>
      <dt className="text-gray-500">{label}</dt>
      <dd className="font-medium text-gray-900">{value}</dd>
    </>
  );
}

function TargetingSection({
  targeting,
  onChange,
  editing,
  locked
}: {
  targeting: TargetingPreview;
  onChange: (t: TargetingPreview) => void;
  editing: boolean;
  locked: boolean;
}) {
  const groupLabels: Record<string, string> = { all: 'Include', any: 'Match Any', none: 'Exclude' };
  const groupColors: Record<string, string> = {
    all: 'bg-green-100 text-green-700',
    any: 'bg-amber-100 text-amber-700',
    none: 'bg-red-100 text-red-700'
  };

  function removeRule(index: number) {
    const rule = targeting.summary[index];
    const newSummary = targeting.summary.filter((_, i) => i !== index);

    const newExpression = structuredClone(targeting.expression);
    const [moduleKey, predicateKey] = rule.key.split('.');
    const mod = newExpression.modules[moduleKey];
    if (mod) {
      const groupObj = mod[rule.group as 'all' | 'any' | 'none'];
      if (groupObj && typeof groupObj === 'object') {
        delete (groupObj as Record<string, unknown>)[predicateKey];
      }
    }

    onChange({ expression: newExpression, summary: newSummary });
  }

  function removeValueFromRule(ruleIndex: number, valueId: string) {
    const rule = targeting.summary[ruleIndex];
    const newValues = rule.values.filter(v => v.id !== valueId);

    if (newValues.length === 0) {
      removeRule(ruleIndex);
      return;
    }

    const newSummary = targeting.summary.map((r, i) =>
      i === ruleIndex ? { ...r, values: newValues } : r
    );

    const newExpression = structuredClone(targeting.expression);
    const [moduleKey, predicateKey] = rule.key.split('.');
    const mod = newExpression.modules[moduleKey];
    if (mod) {
      const groupObj = mod[rule.group as 'all' | 'any' | 'none'];
      if (groupObj && typeof groupObj === 'object') {
        const pred = (groupObj as Record<string, Record<string, unknown>>)[predicateKey];
        if (pred) {
          if (Array.isArray(pred.value)) {
            pred.value = pred.value.filter((v: unknown) => String(v) !== valueId);
          }
        }
      }
    }

    onChange({ expression: newExpression, summary: newSummary });
  }

  if (!editing) {
    return (
      <div className="mt-4 rounded-lg bg-gray-50 p-3">
        <h4 className="mb-2 text-xs font-semibold text-gray-500">TARGETING</h4>
        {targeting.summary.length === 0 ? (
          <div className="text-sm text-gray-400">No targeting rules</div>
        ) : (
          <div className="space-y-1.5">
            {targeting.summary.map((rule, i) => (
              <div key={i} className="rounded border border-gray-200 bg-white px-3 py-2">
                <div className="flex items-center gap-1.5 text-sm">
                  <span
                    className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${groupColors[rule.group] ?? 'bg-gray-100 text-gray-600'}`}>
                    {groupLabels[rule.group] ?? rule.group}
                  </span>
                  <span className="font-medium text-gray-700">{formatTargetingKey(rule.key)}</span>
                </div>
                <div className="mt-1 flex flex-wrap gap-1">
                  {rule.values.map(v => (
                    <span
                      key={v.id}
                      className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600">
                      {v.label ?? v.id}
                    </span>
                  ))}
                </div>
                {rule.reason ? (
                  <div className="mt-1 text-xs text-gray-500 italic">{rule.reason}</div>
                ) : null}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="mt-4 rounded-lg border p-3">
      <h4 className="mb-2 text-xs font-semibold text-gray-500">TARGETING</h4>
      {targeting.summary.length === 0 ? (
        <div className="text-sm text-gray-400">No targeting rules</div>
      ) : (
        <div className="space-y-1.5">
          {targeting.summary.map((rule, i) => (
            <div key={i} className="rounded border border-gray-200 bg-white px-3 py-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1.5 text-sm">
                  <span
                    className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${groupColors[rule.group] ?? 'bg-gray-100 text-gray-600'}`}>
                    {groupLabels[rule.group] ?? rule.group}
                  </span>
                  <span className="font-medium text-gray-700">{formatTargetingKey(rule.key)}</span>
                </div>
                {!locked ? (
                  <button
                    onClick={() => removeRule(i)}
                    className="text-xs text-red-500 hover:text-red-700">
                    Remove
                  </button>
                ) : null}
              </div>
              <div className="mt-1 flex flex-wrap gap-1">
                {rule.values.map(v => (
                  <span
                    key={v.id}
                    className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600">
                    {v.label ?? v.id}
                    {!locked && rule.values.length > 1 ? (
                      <button
                        onClick={() => removeValueFromRule(i, v.id)}
                        className="rounded-full p-0.5 hover:bg-gray-300">
                        <span className="text-[10px] text-gray-400">&times;</span>
                      </button>
                    ) : null}
                  </span>
                ))}
              </div>
              {rule.reason ? (
                <div className="mt-1 text-xs text-gray-500 italic">{rule.reason}</div>
              ) : null}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function formatTargetingKey(key: string): string {
  return key
    .split('.')
    .pop()!
    .replace(/_/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase());
}

function EditField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="mb-0.5 block text-xs text-gray-500">{label}</label>
      {children}
    </div>
  );
}

function computeEditDiff(
  initialCampaign: CreateCampaignPayload | null,
  currentCampaign: CreateCampaignPayload | null,
  initialLineItem: CreateLineItemPayload | null,
  currentLineItem: CreateLineItemPayload
): string | null {
  const changes: string[] = [];

  if (initialCampaign && currentCampaign) {
    if (initialCampaign.name !== currentCampaign.name)
      changes.push(`campaign name to "${currentCampaign.name}"`);
    if (initialCampaign.budget_type !== currentCampaign.budget_type)
      changes.push(`campaign budget type to "${currentCampaign.budget_type}"`);
    if (initialCampaign.spend_budget?.lifetime !== currentCampaign.spend_budget?.lifetime)
      changes.push(`campaign lifetime budget to ${currentCampaign.spend_budget?.lifetime}`);
    if (initialCampaign.start_date !== currentCampaign.start_date)
      changes.push(`campaign start date to ${currentCampaign.start_date}`);
    if (initialCampaign.end_date !== currentCampaign.end_date)
      changes.push(`campaign end date to ${currentCampaign.end_date}`);
  }

  if (initialLineItem) {
    if (initialLineItem.name !== currentLineItem.name)
      changes.push(`line item name to "${currentLineItem.name}"`);
    if (initialLineItem.type !== currentLineItem.type)
      changes.push(`line item type to "${currentLineItem.type}"`);
    if (initialLineItem.budget_type !== currentLineItem.budget_type)
      changes.push(`line item budget type to "${currentLineItem.budget_type}"`);
    if (initialLineItem.spend_budget?.lifetime !== currentLineItem.spend_budget?.lifetime)
      changes.push(`line item lifetime budget to ${currentLineItem.spend_budget?.lifetime}`);
    if (initialLineItem.bidding.cpm_bid !== currentLineItem.bidding.cpm_bid)
      changes.push(`CPM bid to ${formatMoney(currentLineItem.bidding.cpm_bid)}`);
    if (initialLineItem.start_date !== currentLineItem.start_date)
      changes.push(`line item start date to ${currentLineItem.start_date}`);
    if (initialLineItem.end_date !== currentLineItem.end_date)
      changes.push(`line item end date to ${currentLineItem.end_date}`);
  }

  if (changes.length === 0) return null;
  return `Changed ${changes.join(', ')}`;
}
